import LiveStreamService from "services/LiveStreamService";
const { createAsyncThunk, createSlice } = require("@reduxjs/toolkit");

export const getLiveStreams = createAsyncThunk(
  "liveStreams/getAll",
  async ({ page = 1, limit = 10, status, training, instructor, group, q }, { rejectWithValue }) => {
    try {
      const response = await LiveStreamService.getLiveStreams({
        page,
        limit,
        status,
        training,
        instructor,
        group,
        q,
      });
      return {
        liveStreams: response.data.data,
        totalPages: response.data.totalPages,
        currentPage: response.data.page,
        totalItems: response.data.totalItems,
        pageSize: response.data.limit,
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error fetching live streams");
    }
  }
);

export const getLiveStreamById = createAsyncThunk(
  "liveStreams/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await LiveStreamService.getLiveStream(id);
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error fetching live stream");
    }
  }
);

export const createLiveStream = createAsyncThunk(
  "liveStreams/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await LiveStreamService.createLiveStream(data);
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error creating live stream");
    }
  }
);

export const updateLiveStream = createAsyncThunk(
  "liveStreams/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await LiveStreamService.updateLiveStream(id, data);
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error updating live stream");
    }
  }
);

export const deleteLiveStream = createAsyncThunk(
  "liveStreams/delete",
  async (id, { rejectWithValue }) => {
    try {
      await LiveStreamService.deleteLiveStream(id);
      return id;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error deleting live stream");
    }
  }
);

export const getChatMessages = createAsyncThunk(
  "liveStreams/getChatMessages",
  async ({ id, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await LiveStreamService.getChatMessages(id, { page, limit });
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error fetching chat messages");
    }
  }
);

export const addChatMessage = createAsyncThunk(
  "liveStreams/addChatMessage",
  async ({ id, message }, { rejectWithValue }) => {
    try {
      const response = await LiveStreamService.addChatMessage(id, message);
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error adding chat message");
    }
  }
);

export const deleteChatMessage = createAsyncThunk(
  "liveStreams/deleteChatMessage",
  async ({ id, messageId }, { rejectWithValue }) => {
    try {
      await LiveStreamService.deleteChatMessage(id, messageId);
      return messageId;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Error deleting chat message");
    }
  }
);

const initialState = {
  liveStreams: [],
  currentLiveStream: null,
  chatMessages: [],
  loading: false,
  error: null,
  totalPages: 0,
  currentPage: 1,
  totalItems: 0,
  pageSize: 10,
};

const liveStreamsSlice = createSlice({
  name: "liveStreams",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentLiveStream: (state) => {
      state.currentLiveStream = null;
    },
  },
  extraReducers: (builder) => {
    // getLiveStreams
    builder.addCase(getLiveStreams.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getLiveStreams.fulfilled, (state, action) => {
      state.loading = false;
      state.liveStreams = action.payload.liveStreams;
      state.totalPages = action.payload.totalPages;
      state.currentPage = action.payload.currentPage;
      state.totalItems = action.payload.totalItems;
      state.pageSize = action.payload.pageSize;
    });
    builder.addCase(getLiveStreams.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // getLiveStreamById
    builder.addCase(getLiveStreamById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getLiveStreamById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentLiveStream = action.payload;
    });
    builder.addCase(getLiveStreamById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // createLiveStream
    builder.addCase(createLiveStream.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createLiveStream.fulfilled, (state, action) => {
      state.loading = false;
      state.liveStreams.push(action.payload);
      state.liveStreams.sort(
        (a, b) =>
          new Date(a.scheduledStartTime) - new Date(b.scheduledStartTime)
      );
    });
    builder.addCase(createLiveStream.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // updateLiveStream
    builder.addCase(updateLiveStream.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateLiveStream.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.liveStreams.findIndex((l) => l._id === action.payload._id);
      if (index !== -1) {
        state.liveStreams[index] = action.payload;
      }
      state.currentLiveStream = action.payload;
    });
    builder.addCase(updateLiveStream.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // deleteLiveStream
    builder.addCase(deleteLiveStream.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteLiveStream.fulfilled, (state, action) => {
      state.loading = false;
      state.liveStreams = state.liveStreams.filter((l) => l._id !== action.payload);
    });
    builder.addCase(deleteLiveStream.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // getChatMessages
    builder.addCase(getChatMessages.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getChatMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.chatMessages = action.payload;
    });
    builder.addCase(getChatMessages.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // addChatMessage
    builder.addCase(addChatMessage.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addChatMessage.fulfilled, (state, action) => {
      state.loading = false;
      state.chatMessages.push(action.payload);
    });
    builder.addCase(addChatMessage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // deleteChatMessage
    builder.addCase(deleteChatMessage.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteChatMessage.fulfilled, (state, action) => {
      state.loading = false;
      state.chatMessages = state.chatMessages.filter((m) => m._id !== action.payload);
    });
    builder.addCase(deleteChatMessage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const { clearError, clearCurrentLiveStream } = liveStreamsSlice.actions;
export default liveStreamsSlice.reducer;
